import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { interpolateTextAsync } from "../../shared/template";
import type { TemplateContext } from "../../shared/template";
import { useEnduserStore } from "../store/hooks";
import { useOrganization } from "../../shared/store/organizationStore";
import { usePortalSchemaStore, useSeasonBanner, useSeasonRedirectTemplate } from "../../shared/store/portalSchemaStore";
import { consumePendingPartnerRedirect, peekPendingPartnerRedirect } from "../utils/pendingPartnerRedirect";
import { appendRedirectQueryParam } from "../utils/appendRedirectQueryParam";

type PartnerRedirectStatus = "redirecting" | "complete";

/**
 * Owns the post-auth Season auto-redirect side effect. Returns "redirecting"
 * while the JWT mint and template resolution are in flight, and flips to
 * "complete" on success-navigation, missing template, missing session, or
 * resolver failure. The caller decides what to render based on status —
 * lifting state up means failures don't trap the user on a blank page.
 *
 * `enabled` should be passed `isAuthenticated` so the hook is a no-op while
 * the user is on /login, /register, etc.
 */
export const usePartnerRedirect = (enabled: boolean): PartnerRedirectStatus => {
  const { t } = useTranslation("common");
  const session = useEnduserStore((state) => state.session);
  const enduser = useEnduserStore((state) => state.currentEnduser);
  const organization = useOrganization();
  const redirectTemplate = useSeasonRedirectTemplate();
  const seasonBanner = useSeasonBanner();
  const schema = usePortalSchemaStore((state) => state.schema);

  const [status, setStatus] = useState<PartnerRedirectStatus>(() =>
    enabled && peekPendingPartnerRedirect() ? "redirecting" : "complete"
  );
  const ranRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (ranRef.current) return;
    if (status !== "redirecting") return;
    // Wait until the portal schema has loaded — the redirect template lives
    // there and reading it before the fetch settles would falsely look unset.
    if (!schema) return;
    ranRef.current = true;

    const redirectTo = consumePendingPartnerRedirect();
    if (!redirectTo) {
      setStatus("complete");
      return;
    }

    if (!redirectTemplate || !session) {
      toast.error(t("Could not complete redirect. Please try again."));
      setStatus("complete");
      return;
    }

    const templateContext: TemplateContext = {
      enduser,
      organization,
      snippets: {},
    };

    interpolateTextAsync(
      redirectTemplate,
      templateContext,
      { session, enduser, organization },
      { banner: seasonBanner ?? "" }
    )
      .then((resolved) => {
        if (!mountedRef.current) return;
        const finalUrl = appendRedirectQueryParam(resolved, redirectTo);
        window.location.replace(finalUrl);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        toast.error(t("Could not complete redirect. Please try again."));
        setStatus("complete");
      });
  }, [enabled, status, schema, session, enduser, organization, redirectTemplate, seasonBanner, t]);

  return status;
};
